import { waitForElem } from '../../utils/wait_for_elem';
import { observeDOM } from '../../utils/observe_dom';
import { CustomFilter, checkCustomFilter } from '../sei-search-init';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    List?: any;
  }
}

/* Wait for publication-list class (only appears on publication LPs) */
waitForElem('.publication-list').then((publicationList: NodeListOf<HTMLElement>) => {
  const fuzzyDistance = 200;
  const fuzzyThreshold = 0.4;

  /* If publications exist... */
  if (publicationList.length > 0) {
    const pageCount = 10;
    const paginationBottomOptions = {
      name: "paginationBottom",
      paginationClass: "pagination",
      item: '<li><a class="page" href="#resultCount"></a></li>',
      outerWindow: 1,
      innerWindow: 2,
      next: true,
      prev: true,
    };

    const listOptions = {
      valueNames: [
        "title",
        "description",
        "categoryFilter",
        "deliveryFilter",
        "examFilter",
        "dateFilter",
        "certificateFilter",
        "lastNameFilter",
        "yearFilter",
        "serviceFilter",
        "productSuiteFilter",
        "technicalAreaFilter",
        "typeFilter"
      ],
      i: 1,
      page: pageCount,
      pagination: paginationBottomOptions,
      searchClass: "fuzzy-wuzzer",
      fuzzySearch: {
        searchClass: "fuzzy-wuzzy",
        location: 0,
        distance: fuzzyDistance,
        threshold: fuzzyThreshold,
        multiSearch: true,
      },
    }

    // Set list to 5 max per page on sei-bulletin LP
    if (window.location.href.indexOf('sei-bulletin') > 0) {
      listOptions.page = 5;
    }

    // Setup new List object
    const assetList = new window.List('assets', listOptions);
    assetList.on('filterComplete', () => {
      assetList.update();
      setupPageButtonEventListeners();
    })

    const getActivePage = (selector, value: number | boolean =false) => {
      let page = document.querySelector(selector)?.dataset.i ?? 1;
      // Clicked (event target value was passed to function)
      if (value !== false) {
        page = value;
      }
      return page;
    }

    const fuzzyCheck = (item) => {
      const filterKeywordElem = document.getElementById('filter-keyword') as HTMLInputElement;
      if (filterKeywordElem && filterKeywordElem.value != ''){
        if (item.found){
          return true;
        } else {
          return false;
        }
      }
     return true;
    }

    const dateCheck = (item) => {
      let matched = false;
      // Get start and end date from the sidebar filter
      const startDateInput = document.getElementById('startDate')?.querySelector('input');
      const endDateInput   = document.getElementById('endDate')?.querySelector('input');
      // If these elements don't exist on the page, return true (the filter doesn't exist)
      if (startDateInput == null || endDateInput == null) {
        matched = true;
      }
      // Sidebar dateFilter min/max date
      const min_date = startDateInput?.value ?? false;
      const max_date = endDateInput?.value   ?? false;
      /**
       * Check if the item includes a dateFilter (start/end dates).
       * Automatically return true if the item doesn't have a dateFilter configured.
       * If the item has a dateFilter configured, but it's empty (no start/end), then hide these items
       * when the dateFilter is active.
       */
      let dateVals: boolean | string = false;
      if (!('dateFilter' in item.values())) {
        matched = true;
      } else {
        dateVals = item.values()['dateFilter'];
        // Don't match courses if they have no date (and the dateFilter is set)
        if (typeof dateVals === 'string' && dateVals === '') {
          if (min_date || max_date) {
            matched = false;
          } else {
            matched = true;
          }
        }
      }


      // If both start and end have been set in the filter
      if (min_date && max_date) {
        // Create date objects from strings
        const min_unix = parseInt(`${Math.floor(new Date(Date.parse(min_date)).getTime() / 1000)}`, 10);
        const max_unix  = parseInt(`${Math.floor(new Date(Date.parse(max_date)).getTime() / 1000)}`, 10);


        if (typeof dateVals === 'string' && dateVals) {
          let item_start_unix = null
          let item_end_unix = null
          dateVals.split(',').forEach(datestring => {
            item_start_unix = parseInt(`${Math.floor(new Date(datestring.split(';')[0].trim()).getTime() / 1000)}`, 10);
            if (datestring.includes(';')) {
              if (datestring.split(';').length > 1)
                item_end_unix = parseInt(`${Math.floor(new Date(datestring.split(';')[1].trim()).getTime() / 1000)}`, 10);
            }

            if (
              (item_end_unix >= min_unix) &&
              (item_start_unix <= max_unix)
            )
              matched = true;
          });
        } else {
          /* No valid dateFilter dates—
           * don't run through the filter */
          matched = true;
        }
      } else {
        matched = true;
      }
      return matched
    }

    // Set the number of results when filters are manipulated
    const setListCount = (value=1) => {
      waitForElem(".pagination").then(() => {

        // fuzzy search value
        const fuzzy = (document.getElementById('filter-keyword') as HTMLInputElement)?.value ?? '';
        // Show result count
        const listCount = assetList.matchingItems.length ?? 0;
        // Get current page number
        const page = getActivePage("pagination li.active a", value) ?? 1;
        // Get results per page
        const resultsPerPage = (document.getElementById("resultsPerPage") as HTMLInputElement | HTMLSelectElement)?.value ?? 10;
        // results per page dom object
        const resultsPerPageDropDown = document.getElementById("resultsPerPage");
        // No results message
        const noResults = document.getElementById("noResultsMessage");


        // Page number exists, results per page setting exists, list count is above 0
        if (page !== false && resultsPerPage && listCount > 0) {
          let max = (parseInt(`${resultsPerPage}`, 10)*parseInt(page, 10)).toString()
          const min = (parseInt(max, 10)-parseInt(`${resultsPerPage}`, 10)+1).toString()

          // If list is below resultsPerPage, use listCount as max
          if (max > listCount)
            max = listCount;

          // If one result on the current page
          if (parseInt(min, 10) === parseInt(max, 10)) {
            // X of X
            document.getElementById("resultCount").innerHTML = `${max} of ${listCount}`;
          } else {
            // X - X of X
            document.getElementById("resultCount").innerHTML = `${min} - ${max} of ${listCount}`;
          }

          // Change list and pagination display for fuzzy search, filters, and per page changes
          //
          const paginationContainer = document.getElementById('pagination');
          if (fuzzy !== '' && assetList.matchingItems.length > 0){
            if (page){
              assetList.i = page;
            } else {
              assetList.i = 1;
            }
            // assetList.pagination = paginationBottomOptions;

            if (page){
              assetList.show(page, resultsPerPage);
            } else {
              assetList.show(1, resultsPerPage);
            }
          } else if (paginationContainer.innerText === '' && assetList.matchingItems.length > 0){
            assetList.show(1, assetList.matchingItems.length);
            if (!resultsPerPageDropDown.classList?.contains("hidden")) {
              resultsPerPageDropDown.classList.add("hidden");
            }
            document.getElementById("resultCount").innerHTML = 'Displaying all ' + assetList.matchingItems.length + ' results';
          } else if (assetList.matchingItems.length > 0){
            assetList.show(1, assetList.page);
            if (resultsPerPageDropDown.classList?.contains("hidden")) {
              resultsPerPageDropDown.classList.remove("hidden");
            }
            assetList.show(page, resultsPerPage);
          }

          if (parseInt(max, 10) > parseInt(listCount, 10)) {
            if (!paginationContainer.classList?.contains("hidden")) {
              paginationContainer.classList.add("hidden");
            }
          } else {
            if (paginationContainer.classList?.contains("hidden")) {
              paginationContainer.classList.remove("hidden");
            }
          }

          // If results exist, hide "No results" message
          if (!noResults.classList?.contains("hidden")) {
            noResults.classList.add("hidden");
          }
        } else {
          // Hide result count
          document.getElementById("resultCount").innerHTML = '';

          // If no results exist, show "No results" message
          if (noResults.classList?.contains("hidden")) {
            noResults.classList.remove("hidden");
          }
        }
      })
    }

    const filterList = () => {
      const multiFilter = [];

      const filterCheck = (el) => {
        return function(value) {
          return value === el.getAttribute('data-type');
        }
      }

      document.querySelectorAll("input[type='checkbox'][class*='filterCheckbox']:checked, input[type='radio'][class*='filterCheckbox']:checked").forEach((item) => {
        if (
          [
            'categoryFilter',
            'deliveryFilter',
            'examFilter',
            'certificateFilter',
            'lastNameFilter',
            'yearFilter',
            'serviceFilter',
            'productSuiteFilter',
            'technicalAreaFilter',
            'typeFilter'].some(filterCheck(item))
        ) {
          multiFilter.push({
            type: item.getAttribute('data-type'),
            value: (item as HTMLInputElement).value.trim(),
          });
        } else {
          multiFilter.push({
            type: item.getAttribute('data-type'),
            value: (item as HTMLInputElement).value.trim()
          });
        }
      })

      assetList.filter((item) => {
        return (
          checkCustomFilter(new CustomFilter(multiFilter, 'categoryFilter'), item) &&
          checkCustomFilter(new CustomFilter(multiFilter, 'deliveryFilter'), item) &&
          checkCustomFilter(new CustomFilter(multiFilter, 'certificateFilter'), item) &&
          checkCustomFilter(new CustomFilter(multiFilter, 'examFilter'), item) &&
          checkCustomFilter(new CustomFilter(multiFilter, 'lastNameFilter'), item) &&
          checkCustomFilter(new CustomFilter(multiFilter, 'yearFilter'), item) &&
          checkCustomFilter(new CustomFilter(multiFilter, 'serviceFilter'), item) &&
          checkCustomFilter(new CustomFilter(multiFilter, 'productSuiteFilter'), item) &&
          checkCustomFilter(new CustomFilter(multiFilter, 'technicalAreaFilter'), item) &&
          checkCustomFilter(new CustomFilter(multiFilter, 'typeFilter'), item) &&
          dateCheck(item) &&
          fuzzyCheck(item)
        );
      })
      setListCount()
    }

    const filters = [
      "input[type='radio'][class*='filterCheckbox']",
      "input[type='checkbox'][class*='filterCheckbox']"
    ].join(',')

    // Filter the list when a filter checkbox changes
    waitForElem(filters).then((elem: NodeListOf<HTMLElement>) => {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      elem.forEach((match: HTMLInputElement) => {
        match.addEventListener('change', filterList);
        if (urlParams.has('filter')){
          if (urlParams.get('filter').toLocaleLowerCase() == 'exam'){
            urlParams.set('filter', 'yes');
          }
          if (urlParams.get('filter').toLowerCase() == match.getAttribute('value').toLowerCase()){
            match.checked = true;
            match.dispatchEvent(new Event('change'));
            document.getElementById("filter-keyword").scrollIntoView();
          }
        }
      })
    })

    waitForElem('.dateFilter').then((elem: NodeListOf<HTMLElement>) => {
      elem.forEach(match => match.addEventListener('click', filterList));
    })

    // Keyword fuzzy-search
    waitForElem('.fuzzy-wuzzy').then((elem: NodeListOf<HTMLElement>) => {
      elem.forEach(match => match.addEventListener('keyup', filterList));
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);

      // for allowing direct links to a specific keyword search
      // i.e. www.sei.cmu.edu/training/?keyword=principles

      if (urlParams.has('keyword')){
        (document.getElementById('filter-keyword') as HTMLInputElement).value = urlParams.get('keyword').toString();
        document.getElementById('filter-keyword').dispatchEvent(new KeyboardEvent('keyup'));
        document.getElementById("filter-keyword").scrollIntoView();
        // urlParams.delete('keyword');
      }
    })

    // Change range of results when resultsPerPage dropdown is changed
    waitForElem('#resultsPerPage').then(() => {
      const rPP = document.getElementById('resultsPerPage') as HTMLInputElement | HTMLSelectElement;
      rPP.addEventListener('change', () => {
        const selection = rPP.value; // Value of dropdown select
        assetList.update();
        assetList.show(1, selection); // Show 1 through <dropdown_selection_value>
        // Set list count when results per page are changed
        setListCount();
      })
    })


    /* Setup accordions to expand or hide overflow filter values */
    waitForElem(".expandFilter").then((toggle: NodeListOf<HTMLElement>) => {
      toggle.forEach(moreLess => {
        moreLess.addEventListener('click', e => {
          e.preventDefault();
          const parentFilter = (e.target as HTMLElement).closest('.filter');
          if (parentFilter.classList.contains('active')) {
            parentFilter.classList.remove('active');
          } else {
            parentFilter.classList.add('active');
          }
        });
      })
    })

    const setActivePage = (e) => {
      const value = e.target?.dataset?.i ?? false;
      setListCount(value);
    }

    const setupPageButtonEventListeners = () => {
      waitForElem(".pagination li a").then((pageButtons: NodeListOf<HTMLElement>) => {
        pageButtons.forEach(pB => {
          pB.addEventListener('click', setActivePage);
        })
      })
    }

    /* Setup pagination buttons to update current page details */
    waitForElem(".pagination").then(p => {
      // Setup initial list count
      setListCount();
      // Initial event listener setup
      setupPageButtonEventListeners();
      // Re-add event listeners to new pagination buttons when a page is changed
      observeDOM(p[0], m => {
        const addedNodes = [];
        m.forEach(record => record.addedNodes.length & addedNodes.push(...record.addedNodes))
        setupPageButtonEventListeners();
      })
    })

    /* WARN: The 'clear-button' class must be present in the template for reset to work */
    waitForElem(".clear-button").then((cb: NodeListOf<HTMLElement>)  => {
      cb.forEach((button) => {
        button.addEventListener('click', () => {
          // Clear selection of filters
          const filters = document.getElementsByClassName('filterCheckbox');
          Array.from(filters).forEach(filter => {
            (filter as HTMLInputElement).checked = false;
          });
          (document.getElementById('filter-keyword') as HTMLInputElement).value = "";
          // This will trigger the "filterList" function and reset the values
          document.getElementById('filter-keyword').dispatchEvent(new Event('keyup'));
        });
      })
    })
  }
})

/* Course offering selection for when a course has multiple active live offerings */
waitForElem('#registerNowBtn').then(() => {
  const courseSelectButtons = document.querySelectorAll('.courseSelect');
  const regButton = document.getElementById('registerNowBtn');
  courseSelectButtons.forEach(courseSelectButton => {
    courseSelectButton.addEventListener('click', () => {
      regButton.innerHTML = `<a href="${(courseSelectButton as HTMLInputElement).value}" class="mt-4 btn btn-secondary btn-gray btn-block text-center" target="_blank">Register Now</a>`;
    })
  })
})
